import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { mediaQueries } from "../theme";

export const borderColor = ({ theme }) =>
  theme.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)";

export const subtleBg = ({ theme }) =>
  theme.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.025)";

export const STATUS_LABELS = {
  in_progress: "non traité",
  processed: "traité",
  accepted: "validé",
  rejected: "refusé",
  delivered: "livré",
};

export const STATUS_TONES = {
  in_progress: { fg: "#b26a00", bg: "rgba(255, 167, 38, 0.18)" },
  processed: { fg: "#1565c0", bg: "rgba(33, 150, 243, 0.18)" },
  accepted: { fg: "#2e7d32", bg: "rgba(67, 160, 71, 0.18)" },
  delivered: { fg: "#2e7d32", bg: "rgba(67, 160, 71, 0.18)" },
  rejected: { fg: "#c62828", bg: "rgba(229, 57, 53, 0.18)" },
};

export const Section = styled.section`
  background-color: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${borderColor};
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1.5rem;

  ${mediaQueries.tablet} {
    padding: 1.25rem 1.5rem;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 1rem;

  h1,
  h2 {
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0;

    ${mediaQueries.tablet} {
      font-size: 1.2rem;
    }
  }
`;

export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${({ theme }) => theme.primaryOne};
  background-color: ${({ theme }) =>
    theme.mode === "dark"
      ? "rgba(255, 255, 255, 0.12)"
      : "rgba(173, 12, 3, 0.1)"};
`;

export const TableWrapper = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 12px;
  overflow: hidden;

  @media (min-width: 768px) {
    overflow-x: auto;
  }

  @media (max-width: 767.98px) {
    border: none;
    border-radius: 0;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.text};

  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${({ theme }) => theme.card};
    border-bottom: 1px solid ${borderColor};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    opacity: 0.75;
  }

  tbody td {
    border-top: 1px solid ${borderColor};
  }

  tbody tr:first-child td {
    border-top: none;
  }

  tbody tr {
    transition: background-color 0.15s ease-in-out;
  }

  tbody tr:hover {
    background-color: ${subtleBg};
  }

  @media (max-width: 767.98px) {
    display: block;
    font-size: 0.9rem;

    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
    }

    tr {
      border: 1px solid ${borderColor};
      border-radius: 14px;
      background-color: ${({ theme }) => theme.card};
      margin-bottom: 0.75rem;
      padding: 0.35rem 0;
    }

    tbody td,
    tbody tr:first-child td {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      border-top: none;
      padding: 0.4rem 0.9rem;
    }

    td::before {
      content: attr(data-label);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      opacity: 0.6;
    }

    td:empty {
      display: none;
    }
  }
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.primaryOne};
    opacity: 1;
  }

  small {
    font-size: 0.7rem;
    opacity: ${({ $active }) => ($active ? 1 : 0.35)};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.primaryOne};
    `}
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $tone }) => $tone.fg};
  background-color: ${({ $tone }) => $tone.bg};
`;

export const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.65;
`;

export const OrderStatus = ({ status }) => (
  <StatusBadge
    $tone={STATUS_TONES[status] || { fg: "inherit", bg: "transparent" }}
  >
    {STATUS_LABELS[status]}
  </StatusBadge>
);

/**
 * Tri local (affichage uniquement) : tant qu'aucune colonne n'est
 * sélectionnée, les lignes sont rendues dans leur ordre d'origine.
 */
export const useTableSort = (rows, accessors) => {
  const [sort, setSort] = useState({ key: null, direction: "asc" });

  const toggleSort = (key) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedRows = useMemo(() => {
    if (!rows || !sort.key || !accessors[sort.key]) {
      return rows;
    }
    const accessor = accessors[sort.key];
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = accessor(a);
      const right = accessor(b);
      if (left < right) return -1 * factor;
      if (left > right) return 1 * factor;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  return { sortedRows, sort, toggleSort };
};

export const SortableHeaders = ({ columns, sort, onSort, children }) => (
  <tr>
    {columns.map((column) => (
      <th key={column.key} scope={"col"}>
        <SortButton
          type={"button"}
          $active={sort.key === column.key}
          onClick={() => onSort(column.key)}
        >
          {column.label}
          <small>
            {sort.key === column.key
              ? sort.direction === "asc"
                ? "▲"
                : "▼"
              : "↕"}
          </small>
        </SortButton>
      </th>
    ))}
    {children}
  </tr>
);

export const TEXT_ACCESSOR = (field) => (row) =>
  (row[field] || "").toString().toLowerCase();

export const DATE_ACCESSOR = (field) => (row) =>
  new Date(row[field]).getTime() || 0;

export const NUMBER_ACCESSOR = (field) => (row) => {
  const value = parseFloat(String(row[field]).replace(",", "."));
  return isNaN(value) ? 0 : value;
};

export const STATUS_ACCESSOR = (field) => (row) =>
  STATUS_LABELS[row[field]] || "";
